import { React, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Button, Typography, Card, CircularProgress, Dialog, Snackbar, Alert } from '@mui/material';
import ActionButton from '../../lib/ActionButton';
import axios from 'axios';

import styles from './JupyterLink.module.css';

import {
    useGetDatasetsQuery,
} from '../../state/apiSlice';
import Code from '../codeHighlighting';
import MyJWTs from '../secure/MyJWTs';

const jupyterhubUrl = window._env_.REACT_APP_JUPYTERHUB_URL || '';
const jupyterhubSso = window._env_.REACT_APP_JUPYTERHUB_SSO === 'true';
const apiUrl = window._env_.REACT_APP_WCIBAPI_URL || '';

const codeListDataFromDs = (ds) => `from dataportal import DataportalClient

token = '' # Enter your generated JWT

client = DataportalClient(token)
for datafile in client.fromDataset('${ds.DatasetName}').listFiles():
    print(datafile)
`;

export default function JupyterLink() {
    const {
        data: datasets,
        isSuccess,
        isError,
        error
    } = useGetDatasetsQuery();

    const [open, setOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const toggleDialog = () => {
        if (open && searchParams.has('gentokendialog')) {
            searchParams.delete('gentokendialog');
            setSearchParams(searchParams, { replace: true });
        }
        setOpen(!open);
    };

    useEffect(() => {
        if (searchParams.get('gentokendialog') && !open) {
            setOpen(true);
        }
    }, []);

    const selectedID = useSelector(state => state.dataset.selectedID);
    const isLoggedIn = useSelector(state => state.login.value);
    const [ssoLoading, setSsoLoading] = useState(false);
    const [ssoError, setSsoError] = useState('');

    const handleJupyterSso = async () => {
        setSsoLoading(true);
        setSsoError('');
        try {
            const response = await axios.post(`${apiUrl}/v1/jupyterhub/login-token`, {}, { withCredentials: true });
            const { loginUrl } = response.data;
            window.open(loginUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
            const msg = err.response?.data || err.message || 'Failed to get JupyterHub login token';
            setSsoError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setSsoLoading(false);
        }
    };

    let content = <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }} >
        <CircularProgress sx={{ m: 2 }} />
        <Typography align='center'>
            {'Fetching datasets...'}
        </Typography>
    </div>;
    if (isSuccess) {
        let ds;
        if (datasets.filtered.length) {
            ds = datasets.filtered.find(ds => ds.DatasetID === selectedID);
        } else {
            ds = { DatasetName: 'SomeCoolData' };
        }
        if (ds) {
            content = <>
                <Typography variant='body1'>
                    <b>Loading data:</b> The jupyter environment comes preloaded with a python module for loading data from the dataportal into pandas dataframes.
                    First, a token must be generated to allow the library to access the dataportal with your credentials. Press the button below to open the token
                    generation page, create a token and save it to your computer. To then, e.g., list the available files from the dataset {<i>{ds.DatasetName}</i>},
                    simply run
                </Typography>
                <Code language='python' code={codeListDataFromDs(ds)} />
                <Typography variant='body1' paragraph>
                    Run <Code language='python' inline={true} code='help(DataportalClient)'/> to get the full list of features, or visit <Code language='python' inline={true} code='Tutorials/'/> in your Jupyterlab environment.
                </Typography>
            </>;
        }
    } else if (isError) {
        console.error('Failed to fetch datasets: ' + error);
    }

    return <>
        <Typography variant='h4' gutterBottom>
            Access the data files via JupyterLab
        </Typography>
        <Card className={styles.expcard}>
            <Typography variant='body1' paragraph>
                <b>About:</b> WARA-Ops hosts a Juyterhub instance in ERDC to allow users to view and experiment on the datasets that they have access to.
                {jupyterhubSso && isLoggedIn
                    ? ' Press the button below to visit it — you will be automatically logged in with your current session.'
                    : ' Press the button below to visit it, a new login window will appear where you can use the same credentials as for this dataportal.'
                }
                {' '}After logging in a server will be spawned for you that serves a Jupyterlab environment, in where you can write and run notebook and other code
                directly in ERDC. The server will be terminated after 24 hours of idle time, but data saved will persist.
                Some default notebooks are present in the environment, check them out to see how you can get started.
            </Typography>
            {content}
            <Typography variant='body1' paragraph>
                <b>Memory limits:</b> Each Jupyterlab server is granted an upper limit of 64G memory. However, if using more than 16G your server has a high
                probability to get evicted in the unlikely event that the underlying machine runs out of memory. If you plan on running memory
                intensive jobs over longer periods of time, saving your results often becomes even more important.
            </Typography>
            <Typography variant='body1'>
                <b>Updates:</b> The dataportal, JupyterHub and dataportal client are regularly updated.
                Although we strive to keep things backward compatible, your personal Jupyterlab environment is only updated at restart.
                If you suddenly experiences any strange issues with your environment, please stop your server (<i>File -{'>'} Hub Control Panel -{'>'} Stop My Server</i>) and starting it again.
                If any errors persist, please contact the support.
            </Typography>
        </Card>
        <ActionButton onClick={toggleDialog} variant="outlined" size="large" sx={{ margin: 2 }}>
            Generate Token
        </ActionButton>
        {jupyterhubSso && isLoggedIn ? (
            <ActionButton onClick={handleJupyterSso} loading={ssoLoading} variant="outlined" size="large" sx={{ margin: 2 }}>
                {ssoLoading ? 'Opening...' : 'To Jupyterhub!'}
            </ActionButton>
        ) : (
            <Button href={jupyterhubUrl} rel="noopener noreferrer" target="_blank" variant="outlined" size="large" sx={{ margin: 2 }}>
                To Jupyterhub!
            </Button>
        )}
        <Snackbar open={!!ssoError} autoHideDuration={6000} onClose={() => setSsoError('')}>
            <Alert onClose={() => setSsoError('')} severity="error" sx={{ width: '100%' }}>
                {ssoError}
            </Alert>
        </Snackbar>
        <Dialog open={open} onClose={toggleDialog} maxWidth="sm" fullWidth
            PaperProps={{ sx: { maxHeight: '85vh' } }}>
            <MyJWTs onClose={toggleDialog} />
        </Dialog>
    </>;
}
